import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  browserSupportsWebAuthn,
  startAuthentication,
} from '@simplewebauthn/browser';
import { auth } from '../lib/api.js';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next') ?? '/';
  const next = rawNext.startsWith('/dashboard')
    ? rawNext
    : `/dashboard${rawNext === '/' ? '/' : rawNext}`;

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supportsPasskey = browserSupportsWebAuthn();

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await auth.requestMagicLink(email.trim(), next);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onPasskey = async (): Promise<void> => {
    setPasskeyBusy(true);
    setError(null);
    try {
      const begin = await auth.passkeyAuthenticateBegin(email.trim() || undefined);
      // @ts-expect-error — server returns full WebAuthn options shape; types differ slightly across @simplewebauthn versions
      const credResp = await startAuthentication({ optionsJSON: begin.options });
      await auth.passkeyAuthenticateFinish(begin.challenge_id, credResp);
      window.location.replace(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Passkey sign-in failed.';
      // Common cancel = NotAllowedError. Treat as silent (user backed out).
      if (msg.includes('NotAllowed') || msg.toLowerCase().includes('cancel')) {
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-cream-900/30" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[60vw] h-[60vw] max-w-2xl max-h-2xl rounded-full bg-cream/[0.04] blur-3xl" />

      <div className="relative min-h-screen flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-12">
            <h1 className="font-serif text-7xl tracking-tightest text-cream leading-none">Bloom</h1>
            <p className="mt-3 text-xs uppercase tracking-[0.32em] text-cream/40">
              Business is bloomin
            </p>
          </div>

          {submitted ? (
            <div className="border border-cream/15 rounded-2xl px-7 py-8 text-center bg-cream/[0.02]">
              <div className="font-serif text-2xl text-cream mb-2">Check your inbox.</div>
              <p className="text-sm text-cream/60">
                If <span className="text-cream/90">{email}</span> is registered, a sign-in link is on its way.
              </p>
              <p className="mt-3 text-xs text-cream/40">
                Link expires in 15 minutes.
              </p>
              <button
                onClick={() => { setSubmitted(false); setEmail(''); }}
                className="mt-7 text-xs text-cream/50 hover:text-cream transition-colors"
              >
                Use a different email →
              </button>
            </div>
          ) : (
            <>
              {supportsPasskey && (
                <button
                  type="button"
                  onClick={() => { void onPasskey(); }}
                  disabled={passkeyBusy || submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 text-sm font-medium border border-cream/25 text-cream hover:border-cream/60 hover:bg-cream/[0.04] disabled:opacity-50 disabled:cursor-not-allowed transition-all mb-4"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  {passkeyBusy ? 'Waiting for passkey…' : 'Continue with passkey'}
                </button>
              )}

              {supportsPasskey && (
                <div className="flex items-center gap-3 my-5 text-[10px] uppercase tracking-[0.24em] text-cream/30">
                  <span className="flex-1 h-px bg-cream/10" />
                  or email
                  <span className="flex-1 h-px bg-cream/10" />
                </div>
              )}

              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.24em] text-cream/40 mb-3">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email webauthn"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbusiness.com"
                    className="w-full bg-transparent border-0 border-b border-cream/20 px-0 py-3 text-base text-cream placeholder:text-cream/25 focus:outline-none focus:border-cream/60 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || passkeyBusy}
                  className="group w-full inline-flex items-center justify-center gap-2 bg-primary text-black rounded-full py-3 px-6 text-sm font-medium hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {submitting ? 'Sending…' : 'Send sign-in link'}
                  {!submitting && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform group-hover:translate-x-0.5">
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
                {error && <p className="text-xs text-red-400/80">{error}</p>}
              </form>
            </>
          )}

          <p className="mt-16 text-center text-[11px] uppercase tracking-[0.18em] text-cream/30">
            Passwordless · Phishing-resistant{supportsPasskey ? ' · Touch ID enabled' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
